import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { WhatsAppTempFormAtom } from '../../../../Services/_states';

const MessagePreview = () => {
  const whatsappTempFormData = useRecoilValue(WhatsAppTempFormAtom);

  const bodyMessage = () => {
    const body = whatsappTempFormData?.components?.find((obj) => obj?.type === 'BODY');
    if (
      body &&
      body?.text &&
      body?.example?.body_text &&
      body?.example?.body_text?.[0]?.length
    ) {
      const filledText = body?.text.replace(/{{(\d+)}}/g, (match, number) => {
        const index = parseInt(number) - 1;
        return body?.example?.body_text?.[0][index] || match;
      });
      return filledText;
    } else if (body?.text) {
      return body?.text;
    } else return '';
  };

  const headerComponent = whatsappTempFormData?.components?.find(
    (obj) => obj?.type === 'HEADER'
  );
  const headerType = headerComponent?.format;
  const headerText = headerComponent?.text || '';
  const mediaUrl = whatsappTempFormData?.media?.header || null;

  const renderHeaderContent = () => {
    if (!headerComponent) return null;

    if (headerType === 'IMAGE' && mediaUrl) {
      return (
        <Box sx={{ mb: 1 }}>
          <img
            src={mediaUrl}
            alt="Header Image"
            style={{
              width: '100%',
              maxHeight: '200px',
              borderRadius: '8px',
              objectFit: 'contain',
              border: '1px solid #ccc',
            }}
          />
        </Box>
      );
    }

    if (headerType === 'VIDEO' && mediaUrl) {
      return (
        <Box sx={{ mb: 1 }}>
          <video
            controls
            style={{
              width: '100%',
              maxHeight: '240px',
              borderRadius: '8px',
              border: '1px solid #ccc',
            }}
          >
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    if (headerType === 'DOCUMENT' && mediaUrl) {
      const fileName = mediaUrl.split('/').pop();
      return (
        <Box sx={{ mb: 1 }}>
          <a
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '8px 12px',
              borderRadius: '4px',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              color: '#333',
              textDecoration: 'none',
              fontSize: '14px',
              wordBreak: 'break-word',
            }}
          >
            📄 {fileName}
          </a>
        </Box>
      );
    }

    if (headerType === 'TEXT') {
      return (
        <Typography
          variant="body1"
          sx={{
            color: '#000',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          {headerText}
        </Typography>
      );
    }

    return null;
  };

  const footerText = whatsappTempFormData?.components?.find(
  (obj) => obj?.type === 'FOOTER'
)?.text || '';



  const formatMessage = (text) => {
    if (!text) return null;

    const elements = [];
    const regex = /(\*[^*]+\*|_[^_]+_)/g;
    let lastIndex = 0;

    const matches = [...text.matchAll(regex)];

    matches.forEach((match, i) => {
      const matchText = match[0];
      const start = match.index;

      // Push plain text before match
      if (start > lastIndex) {
        elements.push(text.slice(lastIndex, start));
      }

      if (matchText.startsWith('*') && matchText.endsWith('*')) {
        elements.push(<strong key={`bold-${i}`}>{matchText.slice(1, -1)}</strong>);
      } else if (matchText.startsWith('_') && matchText.endsWith('_')) {
        elements.push(<em key={`italic-${i}`}>{matchText.slice(1, -1)}</em>);
      }

      lastIndex = start + matchText.length;
    });

    // Push remaining text after last match
    if (lastIndex < text.length) {
      elements.push(text.slice(lastIndex));
    }

    return elements;
  };

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: '360px',
        margin: '0 auto',
        border: '1px solid #e0e0e0',
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          mb: 1,
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
        }}
      >
        {whatsappTempFormData?.name || 'Template'}
      </Typography>

      {/* WhatsApp-like Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#128C7E',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          Template message
        </Typography>
        <Typography variant="caption">Now</Typography>
      </Box>

      {/* WhatsApp-like Message Bubble */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          backgroundColor: '#dcf8c6',
          padding: '12px 16px',
          borderRadius: '8px',
          margin: '16px 0',
        }}
      >
        {renderHeaderContent()}

        <Typography
          variant="body2"
          sx={{
            color: '#000',
            fontSize: '14px',
            lineHeight: '20px',
            marginTop: '4px',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-line',
          }}
        >
          {formatMessage(bodyMessage()) || 'Body message not available'}
        </Typography>

        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            marginTop: '12px',
            width: {
              lg: '100%',
              md: 'fit-content',
            },
            justifyContent: 'space-between',
            flexDirection: {
              lg: 'row',
              md: 'column',
            },
          }}
        >
          {whatsappTempFormData?.components
            ?.find((obj) => obj?.type === 'BUTTONS')
            ?.buttons?.map((item, index) => (
              <Button
                key={index}
                variant="outlined"
                size="small"
                sx={{
                  color: '#128C7E',
                  borderColor: '#128C7E',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  borderRadius: '4px',
                  fontSize: '12px',
                  width: {
                    lg: '100px',
                    md: 'fit-content',
                  },
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  '&:hover': {
                    backgroundColor: '#e6ffed',
                    borderColor: '#128C7E',
                  },
                }}
              >
                {item?.text || 'Button'}
              </Button>
            ))}
        </Box>
        {footerText && (
  <Typography
    variant="caption"
    sx={{
      marginTop: '12px',
      fontSize: '12px',
      color: '#555',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      width: '100%',
      borderTop: '1px dashed #ccc',
      paddingTop: '8px',
    }}
  >
    {formatMessage(footerText)}
  </Typography>
)}


      </Box>
    </Box>
  );
};

export default MessagePreview;
